import { useEffect, useRef } from 'preact/hooks';
import type { PhaseView } from '@gazette/shared';

export type PhaseOf<N extends PhaseView['name']> = Extract<PhaseView, { name: N }>;

/**
 * Saves as the player types so a timeout keeps what they had. Returns `cancel`, which an
 * explicit send must call: a save still pending from the last keystroke would otherwise
 * land after it and undo the signature.
 */
export function useAutosave<T>(value: T, save: (value: T) => void, delay = 600): () => void {
  const latestSave = useRef(save);
  latestSave.current = save;
  const timer = useRef<number | undefined>(undefined);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    timer.current = window.setTimeout(() => latestSave.current(value), delay);
    return () => window.clearTimeout(timer.current);
  }, [value]);

  return () => window.clearTimeout(timer.current);
}
